///
/// EarthBrowser
///
/// Created by Matt Giger on 9/13/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Shared shell for the anchored info panels. Owns everything the eight
/// dataset panels used to duplicate: the engine position-event
/// subscription, visibility gating against the redux info window, the
/// fixed-position wrapper, the glass card, and the header row (accent
/// pill or title + shared actions). Panels supply a content matcher and
/// a body render function.
///
/// Desktop: a floating card anchored next to the focused entity, placed
/// by the engine (FocusedObjectCallout) with a connector line. Panels that
/// opt in via `scrollable` own pointer events and scroll internally.
///
/// Mobile: a bottom sheet instead. The engine skips placement and the
/// connector; the card docks to the bottom edge, full width, with two
/// states - peek (identity, hero figure, alerts) and expanded (the full
/// body behind the "More details" toggle, scrolling internally). Tap or
/// swipe the handle to switch; swipe down from peek dismisses. The
/// anchored-card approach degraded badly on phones: the placement offset
/// always clamped the card to a screen edge with a connector drawn across
/// the globe, over the entity it described.
///

import { PanelHeaderActions } from "@/components/controls/anchored-panel/PanelHeaderActions"
import { SheetExpansionContext } from "@/components/controls/anchored-panel/panelAtoms"
import { useIsMobile } from "@/components/utils/use-mobile"
import {
	anchoredReactPanelEventName,
	emitAnchoredReactPanelMeasuredHeight,
	requestDismissAnchoredPanel,
	type AnchoredReactPanelState
} from "@/earthbrowser/design-system/AnchoredReactPanelBridge"
import { setMobileInfoSheetHeight, setMobileInfoSheetOpen } from "@/redux/dialogSlice"
import type { InfoWindowContentData } from "@/redux/userInterfaceSlice"
import { useAppDispatch, useAppSelector } from "@/redux/store"
import { cn } from "@/utils/utils"
import { useCallback, useEffect, useMemo, useRef, useState, type TouchEvent } from "react"

const cHiddenPanelState: AnchoredReactPanelState = {
	visible: false,
	x: 0,
	y: 0,
	width: 320,
	height: 280
}

// Mobile sheet height caps (viewport fraction) for the two states; the
// body scrolls inside whichever cap applies
const cSheetPeekMaxVh = 45
const cSheetExpandedMaxVh = 60

// Vertical travel on the handle that counts as a swipe rather than a tap
const cSheetSwipeThresholdPx = 24

export interface PanelShellContext {
	title: string
	subtitle?: string
	/** Mobile: render the reduced layout with details behind MoreSection */
	compact: boolean
}

interface AnchoredPanelShellProps<T> {
	/** Narrow the info-window content to this panel's type, or null */
	match: (data: InfoWindowContentData | null) => T | null
	/** Title fallback when the info window carries none */
	fallbackTitle?: (content: T) => string
	/** Card border accent, static or content-dependent (e.g. buoy category) */
	accentBorder: string | ((content: T) => string)
	/**
	 * Header accent pill: a color class for the standard 3px bar, a render
	 * function for custom pills (storm category color), or null for none.
	 */
	pill?: string | ((content: T) => React.ReactNode) | null
	/** "title" renders the title in the header instead of a pill (location) */
	headerVariant?: "pill" | "title"
	/** Extra header buttons rendered before the shared actions (webcam refresh) */
	headerExtras?: (content: T) => React.ReactNode
	/** Remount the card (and all body state) when this key changes */
	contentKey?: (content: T) => string
	/** Extra card classes, e.g. reduced opacity for stale/defunct sources */
	cardClassName?: (content: T) => string
	/** Card scrolls internally and owns pointer events (hurricane) */
	scrollable?: boolean
	/** Body padding/spacing; panels with denser layouts override it */
	bodyClassName?: string
	/** Reserve the engine's placement height as CSS min-height */
	lockMinHeight?: boolean
	children: (content: T, ctx: PanelShellContext) => React.ReactNode
}

export function AnchoredPanelShell<T>({
	match,
	fallbackTitle,
	accentBorder,
	pill,
	headerVariant = "pill",
	headerExtras,
	contentKey,
	cardClassName,
	scrollable = false,
	bodyClassName = "px-4 pt-3 pb-4 space-y-3",
	lockMinHeight = true,
	children
}: AnchoredPanelShellProps<T>) {
	const [panelState, setPanelState] = useState<AnchoredReactPanelState>(cHiddenPanelState)
	const infoWindow = useAppSelector(state => state.userInterface.infoWindow)
	const compact = useIsMobile()
	const dispatch = useAppDispatch()
	const resizeObserverRef = useRef<ResizeObserver | null>(null)

	// Mobile sheet state: peek vs expanded, shared with MoreSection
	const [expanded, setExpanded] = useState(false)
	const swipeStartY = useRef<number | null>(null)

	useEffect(() => {
		const handlePanelState = (event: Event) => {
			const customEvent = event as CustomEvent<AnchoredReactPanelState>
			setPanelState(customEvent.detail)
		}

		window.addEventListener(anchoredReactPanelEventName, handlePanelState)
		return () => {
			window.removeEventListener(anchoredReactPanelEventName, handlePanelState)
			resizeObserverRef.current?.disconnect()
			resizeObserverRef.current = null
		}
	}, [])

	// Report the card's rendered height back to the engine so the connector
	// line ends at the actual card edge (content height varies; the size
	// table only provides a nominal placement height). Callback ref so the
	// observer follows contentKey remounts. Anchored mode only: the sheet
	// has no connector.
	const cardRef = useCallback((card: HTMLDivElement | null) => {
		resizeObserverRef.current?.disconnect()
		resizeObserverRef.current = null
		if (!card) {
			return
		}

		const observer = new ResizeObserver(() => {
			emitAnchoredReactPanelMeasuredHeight(card.offsetHeight)
		})
		observer.observe(card)
		resizeObserverRef.current = observer
	}, [])

	const content = match(infoWindow?.contentData ?? null)
	const showing = panelState.visible && content !== null

	// Back to peek whenever the sheet switches to a different entity. Keyed
	// on type + title rather than contentData identity, which churns every
	// frame for satellites (live position updates).
	const entityKey = `${infoWindow?.contentType ?? ""}:${infoWindow?.title ?? ""}`
	useEffect(() => {
		setExpanded(false)
	}, [entityKey])

	// The time control shares the bottom edge on phones; tell it to step
	// aside while the sheet is up. Cleanup-then-effect ordering across the
	// sibling shells means a direct switch between entity types ends true.
	useEffect(() => {
		if (!compact || !showing) return
		dispatch(setMobileInfoSheetOpen(true))
		return () => {
			dispatch(setMobileInfoSheetOpen(false))
		}
	}, [compact, showing, dispatch])

	const sheetExpansion = useMemo(() => ({ expanded, setExpanded }), [expanded])

	// Phone sheet: publish the canvas height it covers (viewport bottom to
	// its top edge, so the edge inset counts) as it resizes between peek and
	// expanded; the engine keeps the focused entity above it. Measured on
	// the untransformed wrapper, not the card with its entrance slide
	const sheetObserverRef = useRef<ResizeObserver | null>(null)
	const sheetWrapperRef = useCallback((wrapper: HTMLDivElement | null) => {
		sheetObserverRef.current?.disconnect()
		sheetObserverRef.current = null
		if (!wrapper) return
		const publish = () => {
			dispatch(setMobileInfoSheetHeight(Math.max(0, Math.round(window.innerHeight - wrapper.getBoundingClientRect().top))))
		}
		publish()
		const observer = new ResizeObserver(publish)
		observer.observe(wrapper)
		sheetObserverRef.current = observer
	}, [dispatch])
	useEffect(() => () => {
		sheetObserverRef.current?.disconnect()
		sheetObserverRef.current = null
	}, [])

	if (!showing) {
		return null
	}

	const title = infoWindow?.title ?? fallbackTitle?.(content) ?? ""
	const border = typeof accentBorder === "function" ? accentBorder(content) : accentBorder
	const scrolls = scrollable || compact

	const pillNode = typeof pill === "function"
		? pill(content)
		: pill
			? <div className={cn("h-[3px] w-11 rounded-full", pill)} />
			: null

	const actions = headerExtras ? (
		<div className="flex items-center gap-1">
			{headerExtras(content)}
			<PanelHeaderActions />
		</div>
	) : (
		<PanelHeaderActions className={headerVariant === "title" ? "shrink-0" : undefined} />
	)

	const header = headerVariant === "title" ? (
		<div className={cn("shrink-0 flex items-center justify-between gap-2 px-3 pb-1", compact ? "pt-0.5" : "pt-2")}>
			<div className="text-base leading-tight font-semibold text-white truncate">
				{title}
			</div>
			{actions}
		</div>
	) : (
		<div className={cn("shrink-0 flex items-center justify-between mx-4", compact ? "mt-1" : "mt-3")}>
			{pillNode ?? <div />}
			{actions}
		</div>
	)

	const body = (
		<div className={cn(
			bodyClassName,
			scrolls && "flex-1 min-h-0 overflow-y-auto overscroll-contain [scrollbar-width:thin] [scrollbar-color:rgba(255,255,255,0.2)_transparent]"
		)}>
			{children(content, { title, subtitle: infoWindow?.subtitle, compact })}
		</div>
	)

	if (compact) {
		const toggleExpanded = () => setExpanded(v => !v)

		const handleTouchStart = (event: TouchEvent<HTMLDivElement>) => {
			swipeStartY.current = event.touches[0]?.clientY ?? null
		}

		// Swipe up expands; swipe down collapses, or dismisses from peek.
		// Short travel is a tap, handled by onClick.
		const handleTouchEnd = (event: TouchEvent<HTMLDivElement>) => {
			const startY = swipeStartY.current
			swipeStartY.current = null
			const endY = event.changedTouches[0]?.clientY
			if (startY === null || endY === undefined) return
			const travel = endY - startY
			if (travel < -cSheetSwipeThresholdPx) {
				event.preventDefault()
				setExpanded(true)
			} else if (travel > cSheetSwipeThresholdPx) {
				event.preventDefault()
				if (expanded) {
					setExpanded(false)
				} else {
					requestDismissAnchoredPanel()
				}
			}
		}

		return (
			<div
				ref={sheetWrapperRef}
				className="fixed inset-x-2 z-40 pointer-events-none"
				style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 8px)" }}
			>
				<div
					key={contentKey?.(content)}
					className={cn(
						"pointer-events-auto flex flex-col",
						"bg-black/65 backdrop-blur-xl border rounded-xl shadow-2xl overflow-hidden",
						"transition-[max-height] duration-300 ease-out",
						// Enter from the bottom edge; keyed remounts replay it per entity
						"motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-4 motion-safe:duration-300",
						border,
						cardClassName?.(content)
					)}
					style={{ maxHeight: `${expanded ? cSheetExpandedMaxVh : cSheetPeekMaxVh}vh` }}
				>
					<div
						role="button"
						tabIndex={0}
						aria-label={expanded ? "Collapse details" : "Expand details"}
						aria-expanded={expanded}
						className="shrink-0 flex justify-center pt-2 pb-1 touch-none"
						onClick={toggleExpanded}
						onKeyDown={event => {
							if (event.key === "Enter" || event.key === " ") {
								event.preventDefault()
								toggleExpanded()
							}
						}}
						onTouchStart={handleTouchStart}
						onTouchEnd={handleTouchEnd}
					>
						<div className="h-1 w-10 rounded-full bg-white/30" />
					</div>
					{header}
					<SheetExpansionContext.Provider value={sheetExpansion}>
						{body}
					</SheetExpansionContext.Provider>
				</div>
			</div>
		)
	}

	return (
		<div
			className="fixed z-40 pointer-events-none transition-opacity duration-200"
			style={{
				left: panelState.x,
				top: panelState.y,
				width: `min(${panelState.width}px, calc(100vw - 16px))`,
				...(lockMinHeight && !scrolls ? { minHeight: panelState.height } : {})
			}}
		>
			<div
				key={contentKey?.(content)}
				ref={cardRef}
				className={cn(
					"bg-black/65 backdrop-blur-xl border rounded-xl shadow-2xl overflow-hidden",
					// Fade/grow in on arrival instead of popping; keyed remounts
					// replay it when the focused entity changes
					"motion-safe:animate-in motion-safe:fade-in motion-safe:zoom-in-95 motion-safe:duration-200 ease-out",
					border,
					scrolls && "pointer-events-auto flex flex-col",
					cardClassName?.(content)
				)}
				style={scrolls ? { maxHeight: `calc(100vh - ${panelState.y}px - 12px)` } : undefined}
			>
				{header}
				{body}
			</div>
		</div>
	)
}
